/*
 * @Author: Claude
 * @Date: 2025-11-15
 * @Description: 图片分辨率校验
 */
import { t } from '@/utils/helper/intl';

import { FileUploadRule } from '../types/validator';
import { getImageDimensions } from '../utils/getImageDimensions';
import { parseResolution } from '../utils/parseResolution';

/**
 * 校验图片分辨率
 * @param file - 待校验的文件
 * @param rule - 校验规则
 * @returns Promise<string | null> - 错误信息或 null
 */
export const validateImageResolution = async (
  file: File,
  rule: FileUploadRule,
): Promise<string | null> => {
  const { minResolution, maxResolution, message } = rule;

  // 如果没有设置任何分辨率相关的规则，跳过校验
  if (!minResolution && !maxResolution) {
    return null;
  }

  try {
    const { width, height } = await getImageDimensions(file);

    // 1. 校验最小分辨率
    if (minResolution) {
      const minRes = parseResolution(minResolution);
      if (!minRes) {
        return minResolution;
      }

      if (width < minRes.width || height < minRes.height) {
        return (
          message ||
          t('minResolutionminWidthxminHeight-widthxheight', {
            minResolution,
            minWidth: minRes.width,
            minHeight: minRes.height,
            width,
            height,
          })
        );
      }
    }

    // 2. 校验最大分辨率
    if (maxResolution) {
      const maxRes = parseResolution(maxResolution);
      if (!maxRes) {
        return maxResolution;
      }

      if (width > maxRes.width || height > maxRes.height) {
        return (
          message ||
          t('maxResolutionmaxWidthxmaxHeight-widthxheight', {
            maxResolution,
            maxWidth: maxRes.width,
            maxHeight: maxRes.height,
            width,
            height,
          })
        );
      }
    }

    return null;
  } catch (error) {
    return `${error instanceof Error ? error.message : t('merror.httpError.12301000')}`;
  }
};
